import { useRef, useState, type DragEvent } from 'react';
import { FileUp, Upload } from 'lucide-react';
import { Modal } from '../../components/Modal';
import type { IssueForm } from '../../domain/types';
import type { TranslationKey } from '../../i18n';
import { tryParseIssueFormYaml } from '../../lib/yaml';
import ui from '../../components/ui.module.css';
import styles from './ImportExport.module.css';

const VALID_EXAMPLE = `name: Bug report
description: Report a reproducible problem
body:
  - type: input
    id: summary
    attributes:
      label: Summary
    validations:
      required: true
`;

interface ImportDialogProps {
  open: boolean;
  t: (key: TranslationKey, values?: Record<string, string | number>) => string;
  onClose: () => void;
  onImport: (form: IssueForm) => void;
}

export function ImportDialog({ open, t, onClose, onImport }: ImportDialogProps) {
  const [text, setText] = useState('');
  const [filename, setFilename] = useState('imported-form.yml');
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const readFile = async (file: File) => {
    if (!/\.ya?ml$/i.test(file.name)) {
      setError(t('invalidFileType'));
      return;
    }
    if (file.size > 512 * 1024) {
      setError(t('fileTooLarge'));
      return;
    }
    setFilename(file.name);
    setText(await file.text());
    setError('');
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) void readFile(file);
  };

  const submit = () => {
    const result = tryParseIssueFormYaml(text, filename);
    if (!result.ok) {
      setError(result.error.message);
      return;
    }
    onImport(result.form);
  };

  return (
    <Modal
      open={open}
      title={t('import')}
      closeLabel={t('close')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={ui.button} onClick={onClose}>
            {t('cancel')}
          </button>
          <button
            type="button"
            className={ui.primaryButton}
            data-testid="import-submit"
            disabled={!text.trim()}
            onClick={submit}
          >
            <Upload size={15} strokeWidth={1.8} aria-hidden="true" />
            {t('importYaml')}
          </button>
        </>
      }
    >
      <div
        className={styles.dropZone}
        onDragOver={(event) => event.preventDefault()}
        onDrop={handleDrop}
      >
        <FileUp size={24} strokeWidth={1.6} aria-hidden="true" />
        <strong>{t('dropHere')}</strong>
        <span>{t('importHint')}</span>
        <button type="button" className={ui.button} onClick={() => inputRef.current?.click()}>
          {t('chooseFile')}
        </button>
        <input
          ref={inputRef}
          className={styles.hiddenInput}
          type="file"
          accept=".yml,.yaml,text/yaml,application/yaml"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void readFile(file);
          }}
        />
      </div>

      <div className={styles.separator}>
        <span>{t('pasteYaml')}</span>
      </div>
      <textarea
        className={styles.importText}
        rows={12}
        value={text}
        spellCheck={false}
        data-testid="import-text"
        aria-label={t('pasteYaml')}
        placeholder={VALID_EXAMPLE}
        onChange={(event) => {
          setText(event.target.value);
          setError('');
        }}
      />
      {error ? (
        <div className={styles.importError} role="alert" data-testid="import-error">
          <strong>{t('importFailed')}</strong>
          <p>{error}</p>
          <details>
            <summary>{t('validExample')}</summary>
            <pre>{VALID_EXAMPLE}</pre>
          </details>
        </div>
      ) : null}
    </Modal>
  );
}
